/**
 * Logger utility for Apex Experience
 *
 * Centralizes all console output. In production, only errors are logged.
 * In development, all logs are shown with optional prefixes.
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.log('Message');           // Only in development
 *   logger.warn('Warning');          // Only in development
 *   logger.error('Error');           // Always logged
 *   logger.debug('Debug info');      // Only in development
 *
 * With context:
 *   logger.log('Video loaded', { duration: 10.5, src: '/videos/hero.mp4' });
 */

const isDev = process.env.NODE_ENV === 'development';

type LogArgs = unknown[];

interface Logger {
  log: (...args: LogArgs) => void;
  warn: (...args: LogArgs) => void;
  error: (...args: LogArgs) => void;
  debug: (...args: LogArgs) => void;
  info: (...args: LogArgs) => void;
}

/**
 * Production-safe logger
 * - log, warn, debug, info: Only output in development
 * - error: Always outputs (important for debugging production issues)
 */
export const logger: Logger = {
  log: (...args: LogArgs) => {
    if (isDev) {
      console.log(...args);
    }
  },

  warn: (...args: LogArgs) => {
    if (isDev) {
      console.warn(...args);
    }
  },

  error: (...args: LogArgs) => {
    // Errors always logged - important for production debugging
    console.error(...args);
  },

  debug: (...args: LogArgs) => {
    if (isDev) {
      console.log('[DEBUG]', ...args);
    }
  },

  info: (...args: LogArgs) => {
    if (isDev) {
      console.log('[INFO]', ...args);
    }
  },
};

// Default export for convenience
export default logger;
